import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Lock, Loader2, LogOut, RefreshCw, LayoutDashboard, Inbox, Settings as SettingsIcon, Menu, X, Images } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import useSEO from "../hooks/useSEO";
import Overview from "../components/admin/Overview";
import LeadsView from "../components/admin/LeadsView";
import Settings from "../components/admin/Settings";
import GalleryManager from "../components/admin/GalleryManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mm-admin-token";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "leads", label: "Leads", icon: Inbox },
  { key: "gallery", label: "Gallery", icon: Images },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [pwd, setPwd] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [data, setData] = useState({ leads: [], calls: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { toast } = useToast();
  useSEO({ title: "Dashboard | Miller & Miller Services", description: "Private admin dashboard." });

  const authHeaders = useMemo(() => ({ headers: { "X-Admin-Token": token } }), [token]);

  const fetchLeads = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, { headers: { "X-Admin-Token": token } });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast({ title: "Session expired", description: "Please sign in again." });
      } else {
        toast({ title: "Failed to load leads", description: "Please try again." });
      }
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pwd });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPwd("");
    } catch (err) {
      toast({ title: "Incorrect password", description: "Please try again." });
    } finally {
      setAuthLoading(false);
    }
  };

  const setNewToken = (t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); };
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); setData({ leads: [], calls: [], counts: {} }); };

  const setStatus = async (lead, status) => {
    setData((d) => ({ ...d, leads: d.leads.map((l) => (l.id === lead.id ? { ...l, status } : l)) }));
    try {
      await axios.patch(`${API}/leads/${lead.type}/${lead.id}`, { status }, authHeaders);
      setData((d) => {
        const newCount = d.leads.filter((l) => (l.status || "new") === "new").length;
        return { ...d, counts: { ...d.counts, new: newCount } };
      });
    } catch {
      toast({ title: "Update failed" });
      fetchLeads();
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete this ${lead.type} lead from ${lead.name}?`)) return;
    setData((d) => ({ ...d, leads: d.leads.filter((l) => l.id !== lead.id), counts: { ...d.counts, total: (d.counts.total || 1) - 1, [lead.type]: (d.counts[lead.type] || 1) - 1 } }));
    try {
      await axios.delete(`${API}/leads/${lead.type}/${lead.id}`, authHeaders);
      toast({ title: "Lead deleted" });
    } catch {
      toast({ title: "Delete failed" });
      fetchLeads();
    }
  };

  // ---------- Login ----------
  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-brand-navy px-4">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-2xl">
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-brand-ocean text-white mx-auto"><Lock className="h-7 w-7" /></div>
          <h1 className="mt-5 text-2xl font-heading font-extrabold text-brand-navy text-center">Dashboard Login</h1>
          <p className="mt-1 text-sm text-brand-slate text-center">Miller &amp; Miller Services LLC</p>
          <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Password" autoFocus className="mt-6 h-12" />
          <Button type="submit" disabled={authLoading || !pwd} className="mt-4 w-full h-12 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold">
            {authLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
          </Button>
        </form>
      </div>
    );
  }

  const activeLabel = NAV.find((n) => n.key === view)?.label;

  const SidebarLinks = () => (
    <nav className="space-y-1">
      {NAV.map((n) => {
        const Icon = n.icon;
        const active = view === n.key;
        return (
          <button key={n.key} onClick={() => { setView(n.key); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 h-11 rounded-xl font-heading font-bold text-sm transition-colors ${active ? "bg-brand-ocean text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
            <Icon className="h-5 w-5" /> {n.label}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-brand-mist/40 flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-brand-navy flex-col fixed inset-y-0 left-0 z-30">
        <div className="h-20 flex items-center px-6 border-b border-white/10">
          <img src="https://customer-assets-0z36b82j.emergentagent.net/job_miller-wcag-mobile/artifacts/ha45ga4p_millermiller.png.webp" alt="Miller & Miller" className="h-12 logo-glow" />
        </div>
        <div className="flex-1 p-4"><SidebarLinks /></div>
        <div className="p-4 border-t border-white/10">
          <Button onClick={logout} className="w-full bg-white/10 hover:bg-white/20 text-white h-11"><LogOut className="h-4 w-4 mr-2" /> Sign Out</Button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 inset-y-0 w-64 bg-brand-navy flex flex-col p-4">
            <div className="flex items-center justify-between h-14 mb-2">
              <img src="https://customer-assets-0z36b82j.emergentagent.net/job_miller-wcag-mobile/artifacts/ha45ga4p_millermiller.png.webp" alt="" className="h-10 logo-glow" />
              <button onClick={() => setSidebarOpen(false)} className="h-9 w-9 grid place-items-center rounded-lg bg-white/10 text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1"><SidebarLinks /></div>
            <Button onClick={logout} className="w-full bg-white/10 hover:bg-white/20 text-white h-11"><LogOut className="h-4 w-4 mr-2" /> Sign Out</Button>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="bg-white border-b border-brand-mist sticky top-0 z-20">
          <div className="px-5 sm:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden h-10 w-10 grid place-items-center rounded-lg bg-brand-mist text-brand-navy"><Menu className="h-5 w-5" /></button>
              <h1 className="text-lg font-heading font-extrabold text-brand-navy">{activeLabel}</h1>
            </div>
            <Button onClick={fetchLeads} variant="ghost" className="text-brand-navy hover:bg-brand-mist h-10">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </header>

        <div className="p-5 sm:p-8">
          {view === "overview" && <Overview leads={data.leads} calls={data.calls} counts={data.counts} />}
          {view === "leads" && <LeadsView leads={data.leads} loading={loading} onStatus={setStatus} onDelete={remove} />}
          {view === "gallery" && <GalleryManager token={token} />}
          {view === "settings" && <Settings token={token} onToken={setNewToken} />}
        </div>
      </div>
    </div>
  );
}
